import { METADATA_FIELDS, METADATA_SECTIONS } from '../../../js/courseMetadata'

// Building the surviving course out of a merge group.
//
// A merge decides the survivor's fields on its own: it keeps them when the course was named by a
// human and re-derives name, description, directions and location from the absorbed layouts when it
// was not, and it adopts a member's metadata only when the survivor has none. That routinely throws
// away the best data in the group -- the fuller description is very often on the course being
// absorbed, not on the one that happens to survive.
//
// So the survivor is picked field by field. Everything here is pure: which value wins is a judgement
// the reviewer makes and the editor renders, and none of it should need a component to test.
//
// The draft is deliberately shaped exactly like the wire's SurvivorOverrideDTO -- name,
// secondaryName, description, directions, lat, lng, country, state, metadata -- so submitting is a
// pass-through rather than a second mapping that can disagree with this one.

/** The course-level half. `location` is one pickable unit: half a coordinate pair is not a place. */
export const COURSE_FIELDS = [
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'secondaryName', label: 'Qualifier', type: 'text' },
  { key: 'description', label: 'Description', type: 'block' },
  { key: 'directions', label: 'Directions', type: 'block' },
  { key: 'location', label: 'Coordinates', type: 'location' },
  { key: 'country', label: 'Country code', type: 'number' },
  { key: 'state', label: 'State code', type: 'number' },
]

/** Every field the editor offers, grouped the way it renders them. */
export const SECTIONS = [
  { title: 'Course', fields: COURSE_FIELDS },
  ...METADATA_SECTIONS.map((title) => ({
    title,
    fields: METADATA_FIELDS.filter((field) => field.section === title).map((field) => ({
      ...field,
      metadata: true,
    })),
  })),
]

export const ALL_FIELDS = SECTIONS.flatMap((section) => section.fields)

/** Survivor first, then the courses being absorbed — the order values are preferred in. */
export const coursesOf = (group) => [group.survivor, ...(group.members || [])]

/** What one course says about one field, in the draft's own shape. */
export const valueOf = (course, field) => {
  if (!course) return null
  if (field.metadata) return course.metadata ? course.metadata[field.key] ?? null : null
  if (field.key === 'location') {
    return course.lat != null && course.lng != null ? { lat: course.lat, lng: course.lng } : null
  }
  return course[field.key] ?? null
}

/** Whether a value is worth preferring over the next course's answer. */
export const hasValue = (field, value) => {
  if (value === null || value === undefined) return false
  switch (field.type) {
    case 'multi':
      return Array.isArray(value) && value.length > 0
    case 'bool':
      return value === true || value === false
    case 'location':
      return value.lat != null && value.lng != null
    case 'text':
    case 'block':
    case 'url':
      return String(value).trim() !== ''
    case 'number':
    case 'year':
      // 0 is a real country code (unset) and a nonsense year, so it counts as nothing here.
      return value !== 0
    default:
      return true
  }
}

/** What one course says about a field, once — used by both the picker and combineBest. */
const definedValues = (group, field) =>
  coursesOf(group)
    .map((course) => valueOf(course, field))
    .filter((value) => hasValue(field, value))

/**
 * The combined-best default: the survivor's own value where it has one, otherwise the first course
 * in the group that does. Sets are unioned instead, because two courses each knowing about a
 * different kind of tee is two facts about one place rather than a disagreement.
 *
 * It is a *starting point*, never applied on its own — the reviewer asks for it and then reads it.
 */
export const combineBest = (group) => {
  const draft = {}
  ALL_FIELDS.forEach((field) => {
    const values = definedValues(group, field)
    if (field.type === 'multi') {
      const union = [...new Set(values.flat())].sort((a, b) => a - b)
      if (union.length > 0) setValue(draft, field, union)
      return
    }
    if (values.length > 0) setValue(draft, field, values[0])
  })
  return normalise(draft, group)
}

/** The draft a group starts from when the editor is opened: the survivor exactly as it is today. */
export const survivorDraft = (group) => {
  const draft = {}
  ALL_FIELDS.forEach((field) => {
    const value = valueOf(group.survivor, field)
    if (hasValue(field, value)) setValue(draft, field, value)
  })
  return normalise(draft, group)
}

/** Reads a field out of a draft, undoing the flattening `setValue` does. */
export const getValue = (draft, field) => {
  if (!draft) return null
  if (field.metadata) return draft.metadata ? draft.metadata[field.key] ?? null : null
  if (field.key === 'location') {
    return draft.lat != null && draft.lng != null ? { lat: draft.lat, lng: draft.lng } : null
  }
  return draft[field.key] ?? null
}

/** Writes a field into a draft **in place**. Private; `withValue` is the one callers use. */
const setValue = (draft, field, value) => {
  if (field.metadata) {
    draft.metadata = { ...(draft.metadata || {}), [field.key]: value }
    return
  }
  if (field.key === 'location') {
    draft.lat = value ? value.lat : null
    draft.lng = value ? value.lng : null
    return
  }
  draft[field.key] = value
}

/** A copy of `draft` with one field changed. */
export const withValue = (draft, field, value) => {
  const next = { ...(draft || {}), metadata: draft?.metadata ? { ...draft.metadata } : undefined }
  setValue(next, field, value)
  return next
}

/**
 * The two fields the wire requires, filled in from the survivor when the picker left them empty.
 *
 * `name` and `secondaryName` are non-null on SurvivorOverrideDTO, so a draft that omits them is not
 * a draft the server can read. Everything else is genuinely optional and stays absent.
 */
const normalise = (draft, group) => ({
  ...draft,
  name: draft.name ?? group.survivor.name ?? '',
  secondaryName: draft.secondaryName ?? group.survivor.secondaryName ?? '',
})

/** Whether the group has anything to decide about this field: two courses that disagree. */
export const contested = (group, field) => {
  const seen = coursesOf(group).map((course) => JSON.stringify(valueOf(course, field) ?? null))
  return new Set(seen).size > 1
}

/**
 * The fields a draft changes about the survivor as it stands today, by label.
 *
 * Only the count reaches the card header, but the labels are what make the confirm text say which
 * four fields rather than "4 fields".
 */
export const changedFields = (group, draft) => {
  if (!draft) return []
  const current = survivorDraft(group)
  return ALL_FIELDS.filter((field) => {
    const before = getValue(current, field)
    const after = getValue(draft, field)
    return JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)
  }).map((field) => field.label)
}
